import SearchIcon from '@mui/icons-material/Search'
import {
    Box,
    InputBase,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Modal,
    Paper,
    Typography,
} from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAccount } from '../../context/account_context'
import { useConfigs } from '../../context/configs_context'
import { EmptyState } from '../EmptyState'
import { Navigation, flatNavMenu } from '../LeftPanel/nav'

let isOpen = false
let setOpenFn: ((value: boolean) => void) | null = null
export const isCommandPaletteOpen = () => isOpen
export const setCommandPaletteOpen = (value: boolean) => {
    isOpen = value
}
export const openCommandPalette = () => {
    setOpenFn?.(true)
}

export const CommandPalette = () => {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const [selectedIndex, setSelectedIndex] = useState(0)
    const navigate = useNavigate()
    const { accountInfo } = useAccount()
    const { configs } = useConfigs()
    const inputRef = useRef<HTMLInputElement>(null)

    const options = useMemo(() => {
        const query = search.trim().toLowerCase()

        return flatNavMenu.filter(
            (nav) =>
                accountInfo &&
                nav.validate(accountInfo, configs) &&
                !nav.hide &&
                (!query ||
                    nav.text.toLowerCase().includes(query) ||
                    (nav.description ?? '').toLowerCase().includes(query))
        )
    }, [search, accountInfo, configs])

    const close = () => {
        setOpen(false)
    }

    const toggle = () => {
        setOpen((prev) => !prev)
    }

    const handleNavigate = (nav: Navigation) => {
        close()
        if (nav.link) navigate(nav.link)
        else if (nav.subitems?.length) {
            const subitem = nav.subitems.find(
                (s) => accountInfo && s.validate(accountInfo, configs)
            )
            if (subitem?.link) navigate(subitem.link)
        }
    }

    useEffect(() => {
        setCommandPaletteOpen(open)
        setOpenFn = setOpen
        return () => {
            setOpenFn = null
        }
    }, [open])

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'F11') {
                e.preventDefault()
                e.stopImmediatePropagation()
                toggle()
            }
        }
        document.body.addEventListener('keydown', handler)
        return () => {
            document.body.removeEventListener('keydown', handler)
        }
    }, [])

    useEffect(() => {
        if (!open) return

        setSearch('')
        setSelectedIndex(0)
        setTimeout(() => inputRef.current?.focus(), 50)
    }, [open])

    useEffect(() => {
        if (!open) return

        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault()
                close()
            }
            if (e.key === 'ArrowDown') {
                e.preventDefault()
                setSelectedIndex((i) => Math.min(i + 1, options.length - 1))
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault()
                setSelectedIndex((i) => Math.max(i - 1, 0))
            }
            if (e.key === 'Enter') {
                e.preventDefault()
                if (options[selectedIndex])
                    handleNavigate(options[selectedIndex])
            }
        }
        document.body.addEventListener('keydown', handler)
        return () => {
            document.body.removeEventListener('keydown', handler)
        }
    }, [open, options, selectedIndex])

    return (
        <Modal
            open={open}
            onClose={close}
            componentsProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(15, 23, 42, 0.55)',
                        backdropFilter: 'blur(2px)',
                    },
                },
            }}
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                pt: '12vh',
            }}
        >
            <Paper
                elevation={8}
                sx={{
                    width: { xs: '92%', sm: 600 },
                    maxWidth: '92vw',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    outline: 'none',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: '18px',
                        py: '12px',
                        borderBottom: '1px solid var(--border-color)',
                        backgroundColor: 'var(--surface-soft)',
                    }}
                >
                    <SearchIcon sx={{ color: 'text.secondary', mr: '10px' }} />
                    <InputBase
                        inputRef={inputRef}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value)
                            setSelectedIndex(0)
                        }}
                        placeholder="Pesquisar funcionalidade..."
                        sx={{ flex: 1, fontSize: '16px' }}
                        inputProps={{
                            'aria-label': 'Pesquisar funcionalidade',
                        }}
                    />
                    <Typography
                        fontSize="11px"
                        color="text.secondary"
                        sx={{
                            border: '1px solid var(--border-color)',
                            borderRadius: '6px',
                            px: '6px',
                            py: '2px',
                            fontWeight: 600,
                        }}
                    >
                        F11
                    </Typography>
                </Box>
                <Box sx={{ maxHeight: '55vh', overflowY: 'auto', py: '8px' }}>
                    {options.length === 0 && (
                        <EmptyState
                            icon={<SearchIcon sx={{ fontSize: '26px' }} />}
                            title={t('Sin resultados')}
                            description={t(
                                'No se encontró ninguna funcionalidad'
                            )}
                        />
                    )}
                    {options.map((nav, index) => (
                        <ListItemButton
                            key={`${nav.text}-${index}`}
                            selected={index === selectedIndex}
                            onMouseEnter={() => setSelectedIndex(index)}
                            onClick={() => handleNavigate(nav)}
                            sx={{
                                mx: '8px',
                                borderRadius: '8px',
                                py: '8px',
                                px: '12px',
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 36,
                                    color: 'text.primary',
                                }}
                            >
                                {nav.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={nav.text}
                                secondary={nav.description}
                                primaryTypographyProps={{
                                    fontSize: '14.5px',
                                    fontWeight: 600,
                                }}
                                secondaryTypographyProps={{
                                    fontSize: '12.5px',
                                }}
                            />
                        </ListItemButton>
                    ))}
                </Box>
            </Paper>
        </Modal>
    )
}
